import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  superAdminRoutes,
  orgAdminRoutes,
  type AdminNavItem,
} from './admin-routes';
import { useAdminAuth } from '@/store/admin/admin-auth-slice';

/**
 * Admin shell — persistent sidebar rail + header + routed content area.
 *
 * Composition follows DS-002: Block composes from Atoms. We use DS `NavItem`
 * primitives (via the Link wrappers below) rather than the scheduling-app
 * SideNav block, which hardcodes its own item list.
 *
 * Sidebar: 224px wide, dark surface, role-aware.
 * Header:  48px tall, displays logged-in user + logout.
 * Content: scroll-y, sits in the FHIR-neutral surface background.
 */
export function AdminShell({ children }: { children: ReactNode }) {
  const { role, claims, clear } = useAdminAuth();
  const items: AdminNavItem[] =
    role === 'superAdmin' ? superAdminRoutes : orgAdminRoutes;

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <AdminSidebar items={items} />
      <div className="flex-1 flex flex-col min-w-0">
        <AdminHeader
          userId={claims?.sub}
          role={role === 'superAdmin' ? 'Super Admin' : role === 'orgAdmin' ? 'Org Admin' : ''}
          onLogout={clear}
        />
        <main className="flex-1 overflow-auto scrollbar-hide bg-[var(--color-surface-background)] px-6 py-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function AdminSidebar({ items }: { items: AdminNavItem[] }) {
  const location = useLocation();
  return (
    <nav
      className="w-56 flex-shrink-0 flex flex-col gap-1 bg-[var(--surface-body-card-dark)] text-white/90 px-3 py-4"
      aria-label="Admin navigation"
    >
      <div className="flex items-center gap-2 px-2 mb-6">
        <div className="size-8 rounded-md bg-[var(--accent-primary)] flex items-center justify-center text-[var(--surface-read)] [font:var(--type-body-strong)]">
          S
        </div>
        <span className="[font:var(--type-body-strong)]">Scrips Admin</span>
      </div>

      {items.map((item) => {
        const isActive = location.pathname.startsWith(item.path);
        const Icon = item.icon;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-2 [font:var(--type-body)] transition-colors',
              isActive
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:text-white hover:bg-white/10',
            )}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon className="h-4 w-4" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function AdminHeader({
  userId,
  role,
  onLogout,
}: {
  userId?: string;
  role: string;
  onLogout: () => void;
}) {
  return (
    <header className="h-12 flex-shrink-0 flex items-center justify-between border-b border-[var(--surface-line)] bg-[var(--surface-read)] px-6">
      <div className="[font:var(--type-body-strong)] text-[var(--ink-body)]">
        {role}
      </div>
      <div className="flex items-center gap-3">
        {userId && (
          <span className="[font:var(--type-body)] text-[var(--ink-meta)]" title={userId}>
            {userId.slice(0, 8)}…
          </span>
        )}
        <button
          type="button"
          onClick={onLogout}
          className="[font:var(--type-body)] text-[var(--accent-primary)] hover:underline"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}
